import React, { useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import WizardLayout from '../../../../common/components/wizard-layout/WizardLayout';
import Icon from '../../../../common/components/icon/Icon';
import { mapStyles, mapStyleTypes } from '../../../utils/constants/map-styles';
import { useGlMap } from '../../../contexts/context';
import { selectStyle, styleChanged } from '../../../store';
import {
  styleSelectorShownChanged,
  selectStyleSelectorShown,
} from '../../../../common/store/ui';
import Overlay from '../../../../common/components/overlay/Overlay';
import Responsive from '../../../../common/components/responsive/Responsive';

import './StyleSwitchControl.scss';

const mapStylesList = [
  mapStyles[mapStyleTypes.streets],
  mapStyles[mapStyleTypes.satellite],
  mapStyles[mapStyleTypes.hybrid],
];

export interface StyleSwitchControlProps {
  className?: string;
}

const StyleSwitchControl = ({ className }: StyleSwitchControlProps) => {
  const styleSelectorShown = useSelector(selectStyleSelectorShown);
  const dispatch = useDispatch();
  const setStyleSelectorShown = useCallback(
    (isSelectorShown) => dispatch(styleSelectorShownChanged(isSelectorShown)),
    [dispatch],
  );

  useEffect(() => {
    // handle initial load and disable menu
    setStyleSelectorShown(false);

    return () => {
      setStyleSelectorShown(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleMenu = useCallback(
    () => setStyleSelectorShown(!styleSelectorShown),
    [setStyleSelectorShown, styleSelectorShown],
  );

  const currentStyle = useSelector(selectStyle);
  const glMap = useGlMap();

  const toggleStyle = useCallback(
    (style) => {
      dispatch(styleChanged(style));

      if (glMap) {
        glMap.setStyle(style.url);
      }
    },
    [glMap, dispatch],
  );

  const styleSelector = (
    <div className="style-switch-control__style-selector">
      {mapStylesList.map((style) => (
        <button
          key={style.id}
          className={classnames('style-switch-control__style-button', {
            'style-switch-control__style-button_active-style':
              currentStyle.id === style.id,
          })}
          type="button"
          disabled={currentStyle.id === style.id}
          onClick={() => toggleStyle(style)}
        >
          {style.name}
        </button>
      ))}
    </div>
  );

  return (
    <div className={classnames('style-switch-control', className)}>
      <button
        className={classnames(
          'style-switch-control__style-selector-toggle-button',
          {
            'style-switch-control__style-selector-toggle-button_active':
              styleSelectorShown,
          },
        )}
        type="button"
        onClick={handleToggleMenu}
      >
        <Icon
          className="style-switch-control__style-selector-toggle-button-icon"
          name="layers"
        />
      </button>
      {styleSelectorShown && (
        <>
          <Responsive>
            <Overlay
              className="style-switch-control__style-selector-overlay"
              position="bottom"
              actionable={true}
            >
              <WizardLayout
                className="style-switch-control__style-selector-layout"
                title="Map style"
                hasCloseButton={true}
                onCloseButtonClick={handleToggleMenu}
                shouldDispatchHeightChange={false}
              >
                {styleSelector}
              </WizardLayout>
            </Overlay>
          </Responsive>
          <Responsive mobile={false}>{styleSelector}</Responsive>
        </>
      )}
    </div>
  );
};

export default StyleSwitchControl;
